import React, { useEffect, useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import {
  Building2,
  ChevronLeft,
  ChevronRight,
  CheckCircle2,
  CheckCircle,
  Mail,
  RefreshCw,
  Search,
  UserPlus,
  Users,
  X,
  Upload,
  File,
  Trash2,
  Download,
  Eye,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import api, { uploadRequestConfig } from '../../services/api';
import { MAX_FILE_MB, MAX_FILE_SIZE_BYTES } from '../../config/config';
import LoadingSpinner from '../common/LoadingSpinner';
import { toast } from 'react-toastify';
import { getUserIdentifier, normalizeUserProfiles } from '../../utils/userProfile';
import { ORGANIZATION_OPTIONS } from '../../constants/organizations';
import { viewDocument, downloadDocument } from '../../utils/documentAccess';
import {
  DOCUMENT_TYPE_OPTIONS,
  DOCUMENT_TYPE_OTHER,
  DOCUMENT_LABEL_MAX_LENGTH,
  DOCUMENT_LABEL_MIN_LENGTH,
  documentDisplayName,
} from '../../constants/documentTypes';
import {
  derivePayrollValues,
  formatCurrencyPreview,
  PAYROLL_PERCENTAGE_FIELDS,
  PAYROLL_PERCENTAGE_INPUT_FIELDS,
} from '../../utils/payrollUtils';

// ─────────────────────────────────────────────────────────────────────────────
// CONSTANTS
// ─────────────────────────────────────────────────────────────────────────────

const INITIAL_FORM_DATA = {
  organization:            '',
  employeeId:              '',
  name:                    '',
  dateOfBirth:             '',
  gender:                  '',
  maritalStatus:           '',
  jobTitle:                '',
  joinDate:                '',
  password:                '',
  physicalChallenged:      false,
  bloodGroup:              '',
  panNumber:               '',
  aadhaarNumber:           '',
  passportNumber:          '',
  email:                   '',
  mobileNumber:            '',
  alternateNumber:         '',
  emergencyContactNumber:  '',
  emergencyName:           '',
  emergencyRelation:       '',
  presentAddress:          '',
  permanentAddress:        '',
  city:                    '',
  state:                   '',
  pinCode:                 '',
  ctcBasic:                '',
  ctcHRAPercentage:        '',
  ctcHRA:                  '',
  ctcSpecialAllowancePercentage: '',
  ctcSpecialAllowance:     '',
  ctcTravelAllowancePercentage: '',
  ctcTravelAllowance:      '',
  ctcITAllowancePercentage: '',
  ctcITAllowance:          '',
  payScale:                '',
  pfDetails:               '',
  medicalInsurance:        '',
  schoolName:                       '',
  schoolBoard:                      '',
  schoolYearOfGraduation:           '',
  schoolPercentageOrCGPA:           '',
  universityName:                   '',
  universityBoard:                  '',
  universityYearOfGraduation:       '',
  universityPercentageOrCGPA:       '',
  postGraduationName:               '',
  postGraduationBoard:              '',
  postGraduationYearOfGraduation:   '',
  postGraduationPercentageOrCGPA:   '',
  highestEducationCategory:         '',
  highestEducationInstitutionName:  '',
  highestEducationBoard:            '',
  highestEducationYearOfPassing:    '',
  highestEducationPercentageOrCGPA: '',
  totalExperience:            '',
  previousCompanyName:        '',
  previousCompanyLocation:    '',
  previousJobTitle:           '',
  previousSupervisorName:     '',
  previousSupervisorContact:  '',
  previousStartDate:          '',
  previousEndDate:            '',
  reasonForLeaving:           '',
  relievingLetterSubmitted:   false,
  reference1Name:             '',
  reference1Address:          '',
  reference1Mobile:           '',
  reference1Relationship:     '',
  reference2Name:             '',
  reference2Address:          '',
  reference2Mobile:           '',
  reference2Relationship:     '',
};

const NON_NEGATIVE_NUMBER_FIELDS = [
  'ctcBasic', 'ctcHRA', 'ctcSpecialAllowance',
  'ctcTravelAllowance', 'ctcITAllowance', 'payScale',
];
const YEAR_FIELDS = [
  'schoolYearOfGraduation', 'universityYearOfGraduation',
  'postGraduationYearOfGraduation', 'highestEducationYearOfPassing',
];
const NUMERIC_FIELDS = [...NON_NEGATIVE_NUMBER_FIELDS, ...YEAR_FIELDS];

const STEPS = [
  {
    id: 1,
    label: 'Personal',
    description: 'Identity & credentials',
    requiredFields: [
      'organization', 'name', 'dateOfBirth', 'gender', 'maritalStatus',
      'jobTitle', 'password', 'employeeId', 'panNumber', 'aadhaarNumber',
    ],
  },
  {
    id: 2,
    label: 'Address',
    description: 'Contact & location',
    requiredFields: [
      'email', 'mobileNumber', 'alternateNumber',
      'emergencyName', 'emergencyRelation', 'emergencyContactNumber',
      'presentAddress', 'permanentAddress', 'city', 'state', 'pinCode',
    ],
  },
  {
    id: 3,
    label: 'Payroll',
    description: 'CTC & compliance',
    requiredFields: [
      'ctcBasic', 'ctcHRA', 'ctcSpecialAllowance',
      'ctcTravelAllowance', 'ctcITAllowance',
    ],
  },
  {
    id: 4,
    label: 'Education',
    description: 'Academic background',
    requiredFields: [],
  },
  {
    id: 5,
    label: 'Experience',
    description: 'Work history & references',
    requiredFields: [],
  },
  {
    id: 6,
    label: 'Documents',
    description: 'Upload employee documents',
    requiredFields: [],
  },
];

const FIELD_LABELS = {
  organization: 'Organization',
  name: 'Name', dateOfBirth: 'Date of Birth', gender: 'Gender',
  maritalStatus: 'Marital Status', jobTitle: 'Job Title',
  employeeId: 'Employee ID', panNumber: 'PAN Number',
  aadhaarNumber: 'Aadhaar Number', password: 'Password',
  email: 'Email', mobileNumber: 'Mobile Number',
  alternateNumber: 'Alternate Number',
  emergencyContactNumber: 'Emergency Contact Number',
  presentAddress: 'Present Address', permanentAddress: 'Permanent Address',
  city: 'City', state: 'State', pinCode: 'Pin Code',
  ctcBasic: 'CTC Basic',
  ctcHRAPercentage: 'HRA Percentage', ctcHRA: 'CTC HRA',
  ctcSpecialAllowancePercentage: 'Special Allowance Percentage',
  ctcSpecialAllowance: 'CTC Special Allowance',
  ctcTravelAllowancePercentage: 'Travel Allowance Percentage',
  ctcTravelAllowance: 'CTC Travel Allowance',
  ctcITAllowancePercentage: 'IT Allowance Percentage',
  ctcITAllowance: 'CTC IT Allowance',
};

const inputCls =
  'w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500';

const formatFileSize = (bytes) => {
  if (!bytes || bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
};

// ─────────────────────────────────────────────────────────────────────────────
// STEPPER HEADER
// ─────────────────────────────────────────────────────────────────────────────
const StepHeader = ({ currentStep }) => (
  <div className="flex items-center px-6 py-4 border-b border-slate-200 bg-slate-50">
    {STEPS.map((step, idx) => {
      const done    = currentStep > step.id;
      const current = currentStep === step.id;
      return (
        <React.Fragment key={step.id}>
          <div className="flex flex-col items-center gap-1">
            <div className={`flex h-9 w-9 items-center justify-center rounded-full text-sm font-bold transition-all ${
              done    ? 'bg-emerald-500 text-white' :
              current ? 'bg-primary-600 text-white shadow-md shadow-primary-200' :
                        'bg-slate-200 text-slate-500'
            }`}>
              {done ? <CheckCircle2 className="h-5 w-5" /> : step.id}
            </div>
            <span className={`hidden sm:block text-xs font-medium text-center w-16 truncate ${
              current ? 'text-primary-700' : done ? 'text-emerald-600' : 'text-slate-400'
            }`}>
              {step.label}
            </span>
          </div>
          {idx < STEPS.length - 1 && (
            <div className={`h-0.5 flex-1 mx-2 rounded-full transition-all ${
              currentStep > step.id ? 'bg-emerald-400' : 'bg-slate-200'
            }`} />
          )}
        </React.Fragment>
      );
    })}
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// STEP 1 – PERSONAL
// ─────────────────────────────────────────────────────────────────────────────
const StepPersonal = ({ formData, handleInputChange, handleOrganizationChange, generatingEmployeeId }) => (
  <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
    <div>
      <label className="mb-1 block text-sm font-medium text-slate-700">Organization *</label>
      <select value={formData.organization}
        onChange={(e) => handleOrganizationChange(e.target.value)} className={inputCls}>
        <option value="">Select organization</option>
        {ORGANIZATION_OPTIONS.map((org) => <option key={org} value={org}>{org}</option>)}
      </select>
    </div>

    <div>
      <label className="mb-1 block text-sm font-medium text-slate-700">
        Employee ID *{' '}
        {generatingEmployeeId && <span className="text-xs text-primary-500">Generating…</span>}
      </label>
      <input type="text" readOnly value={formData.employeeId}
        className={`${inputCls} cursor-not-allowed bg-slate-50 text-slate-600`}
        placeholder="Auto-generated after organisation selection" />
    </div>

    <div>
      <label className="mb-1 block text-sm font-medium text-slate-700">Full Name *</label>
      <input type="text" required value={formData.name}
        onChange={(e) => handleInputChange('name', e.target.value)} className={inputCls} />
    </div>

    <div>
      <label className="mb-1 block text-sm font-medium text-slate-700">Date of Birth *</label>
      <input type="date" required value={formData.dateOfBirth}
        onChange={(e) => handleInputChange('dateOfBirth', e.target.value)} className={inputCls} />
    </div>

    <div>
      <label className="mb-1 block text-sm font-medium text-slate-700">Gender *</label>
      <select required value={formData.gender}
        onChange={(e) => handleInputChange('gender', e.target.value)} className={inputCls}>
        <option value="">Select gender</option>
        <option value="male">Male</option>
        <option value="female">Female</option>
        <option value="other">Other</option>
      </select>
    </div>

    <div>
      <label className="mb-1 block text-sm font-medium text-slate-700">Marital Status *</label>
      <select required value={formData.maritalStatus}
        onChange={(e) => handleInputChange('maritalStatus', e.target.value)} className={inputCls}>
        <option value="">Select status</option>
        <option value="single">Single</option>
        <option value="married">Married</option>
      </select>
    </div>

    <div>
      <label className="mb-1 block text-sm font-medium text-slate-700">Job Title *</label>
      <input type="text" required value={formData.jobTitle}
        onChange={(e) => handleInputChange('jobTitle', e.target.value)} className={inputCls} />
    </div>

    <div>
      <label className="mb-1 block text-sm font-medium text-slate-700">Join Date</label>
      <input type="date" value={formData.joinDate}
        onChange={(e) => handleInputChange('joinDate', e.target.value)} className={inputCls} />
    </div>

    <div>
      <label className="mb-1 block text-sm font-medium text-slate-700">
        Password * <span className="text-xs text-slate-400">(min 6 chars)</span>
      </label>
      <input type="password" required minLength={6} value={formData.password}
        onChange={(e) => handleInputChange('password', e.target.value)} className={inputCls} />
    </div>

    <div>
      <label className="mb-1 block text-sm font-medium text-slate-700">PAN Number *</label>
      <input type="text" required value={formData.panNumber}
        onChange={(e) => handleInputChange('panNumber', e.target.value.toUpperCase())}
        className={inputCls} placeholder="ABCDE1234F" />
    </div>

    <div>
      <label className="mb-1 block text-sm font-medium text-slate-700">Aadhaar Number *</label>
      <input type="text" required value={formData.aadhaarNumber}
        onChange={(e) => handleInputChange('aadhaarNumber', e.target.value)}
        className={inputCls} placeholder="12-digit number" />
    </div>

    <div>
      <label className="mb-1 block text-sm font-medium text-slate-700">Passport Number</label>
      <input type="text" value={formData.passportNumber}
        onChange={(e) => handleInputChange('passportNumber', e.target.value)} className={inputCls} />
    </div>

    <div>
      <label className="mb-1 block text-sm font-medium text-slate-700">Blood Group</label>
      <select value={formData.bloodGroup}
        onChange={(e) => handleInputChange('bloodGroup', e.target.value)} className={inputCls}>
        <option value="">Select</option>
        {['A+','A-','B+','B-','AB+','AB-','O+','O-'].map((bg) =>
          <option key={bg} value={bg}>{bg}</option>)}
      </select>
    </div>

    <div className="flex items-center gap-3 pt-7">
      <input id="physicalChallenged" type="checkbox"
        checked={formData.physicalChallenged}
        onChange={(e) => handleInputChange('physicalChallenged', e.target.checked)}
        className="h-4 w-4 rounded border-slate-300 text-primary-600 focus:ring-primary-500" />
      <label htmlFor="physicalChallenged" className="text-sm font-medium text-slate-700">
        Physical Challenged
      </label>
    </div>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// STEP 2 – ADDRESS & CONTACT
// ─────────────────────────────────────────────────────────────────────────────
const StepAddress = ({ formData, handleInputChange }) => (
  <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
    {[
      ['email',                  'Email *',              'email'],
      ['mobileNumber',           'Mobile Number *',      'tel'],
      ['alternateNumber',        'Alternate Number *',   'tel'],
      
      ['emergencyName', 'Emergency Name *', 'text'],
      ['emergencyRelation', 'Emergency Relation *', 'text'],
      ['emergencyContactNumber', 'Emergency Contact *', 'tel'],

      ['city',                   'City *',               'text'],
      ['state',                  'State *',              'text'],
      ['pinCode',                'Pin Code *',           'text'],
    ].map(([field, label, type]) => (
      <div key={field}>
        <label className="mb-1 block text-sm font-medium text-slate-700">{label}</label>
        <input type={type} required value={formData[field]}
          onChange={(e) => handleInputChange(field, e.target.value)} className={inputCls} />
      </div>
    ))}
    <div className="md:col-span-3">
      <label className="mb-1 block text-sm font-medium text-slate-700">Present Address *</label>
      <textarea required rows={2} value={formData.presentAddress}
        onChange={(e) => handleInputChange('presentAddress', e.target.value)} className={inputCls} />
    </div>
    <div className="md:col-span-3">
      <label className="mb-1 block text-sm font-medium text-slate-700">Permanent Address *</label>
      <textarea required rows={2} value={formData.permanentAddress}
        onChange={(e) => handleInputChange('permanentAddress', e.target.value)} className={inputCls} />
    </div>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// STEP 3 – PAYROLL
// ─────────────────────────────────────────────────────────────────────────────
const StepPayroll = ({ formData, handleInputChange }) => (
  <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
    <div>
      <label className="mb-1 block text-sm font-medium text-slate-700">Basic Salary *</label>
      <div className="relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm">Rs</span>
        <input type="number" min="0" step="0.01" required
          value={formData.ctcBasic}
          onChange={(e) => handleInputChange('ctcBasic', e.target.value)}
          className={`${inputCls} pl-10`} />
      </div>
      <p className="mt-1 text-xs text-slate-500">Enter the base salary amount. All allowances are derived from this.</p>
    </div>

    {PAYROLL_PERCENTAGE_FIELDS.map(({ percentageField, amountField, label }) => (
      <div key={percentageField} className="rounded-lg border border-slate-200 bg-slate-50 p-3">
        <label className="mb-1 block text-sm font-medium text-slate-700">{label} % *</label>
        <div className="relative">
          <input type="number" min="0" step="0.01" required
            value={formData[percentageField]}
            onChange={(e) => handleInputChange(percentageField, e.target.value)}
            className={`${inputCls} pr-9 bg-white`}
            placeholder="e.g. 30" />
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm">%</span>
        </div>
        <p className="mt-2 text-xs font-medium text-slate-600">Amount: <span className="text-slate-900">{formatCurrencyPreview(formData[amountField])}</span></p>
      </div>
    ))}

    <div>
      <label className="mb-1 block text-sm font-medium text-slate-700">CTC</label>
      <div className="relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm">Rs</span>
        <input type="number" min="0" step="0.01"
          value={formData.payScale}
          readOnly
          className={`${inputCls} pl-10 cursor-not-allowed bg-slate-100 text-slate-700`} />
      </div>
      <p className="mt-1 text-xs text-slate-500">Auto-calculated as basic plus all allowance amounts.</p>
    </div>

    <div>
      <label className="mb-1 block text-sm font-medium text-slate-700">PF Details</label>
      <input type="text" value={formData.pfDetails}
        onChange={(e) => handleInputChange('pfDetails', e.target.value)}
        className={inputCls} placeholder="UAN / account number" />
    </div>
    <div>
      <label className="mb-1 block text-sm font-medium text-slate-700">Medical Insurance</label>
      <input type="text" value={formData.medicalInsurance}
        onChange={(e) => handleInputChange('medicalInsurance', e.target.value)}
        className={inputCls} placeholder="Policy number / provider" />
    </div>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// STEP 4 – EDUCATION
// ─────────────────────────────────────────────────────────────────────────────
const StepEducation = ({ formData, handleInputChange }) => (
  <div className="space-y-5">
    {[
      {
        heading: 'School (10th / 12th)',
        fields: [
          ['schoolName',             'School Name',        'text'],
          ['schoolBoard',            'Board',              'text'],
          ['schoolYearOfGraduation', 'Year of Graduation', 'number'],
          ['schoolPercentageOrCGPA', 'Percentage / CGPA',  'text'],
        ],
      },
      {
        heading: 'Graduation (UG)',
        fields: [
          ['universityName',             'University Name',     'text'],
          ['universityBoard',            'Board / Affiliation', 'text'],
          ['universityYearOfGraduation', 'Year of Graduation',  'number'],
          ['universityPercentageOrCGPA', 'Percentage / CGPA',   'text'],
        ],
      },
      {
        heading: 'Post Graduation (PG)',
        fields: [
          ['postGraduationName',             'Course / University', 'text'],
          ['postGraduationBoard',            'Board / Affiliation', 'text'],
          ['postGraduationYearOfGraduation', 'Year of Graduation',  'number'],
          ['postGraduationPercentageOrCGPA', 'Percentage / CGPA',   'text'],
        ],
      },
      {
        heading: 'Highest Education',
        fields: [
          ['highestEducationCategory',        'Category',          'text'],
          ['highestEducationInstitutionName',  'Institution',       'text'],
          ['highestEducationBoard',            'Board',             'text'],
          ['highestEducationYearOfPassing',    'Year of Passing',   'number'],
          ['highestEducationPercentageOrCGPA', 'Percentage / CGPA', 'text'],
        ],
      },
    ].map((section) => (
      <div key={section.heading} className="rounded-lg border border-slate-200 p-4">
        <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-500">
          {section.heading}
        </p>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
          {section.fields.map(([field, label, type]) => (
            <div key={field}>
              <label className="mb-1 block text-xs font-medium text-slate-600">{label}</label>
              <input type={type}
                min={type === 'number' ? 1900 : undefined}
                max={type === 'number' ? 2100 : undefined}
                value={formData[field]}
                onChange={(e) => handleInputChange(field, e.target.value)}
                className={inputCls} />
            </div>
          ))}
        </div>
      </div>
    ))}
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// STEP 5 – EXPERIENCE & REFERENCES
// ─────────────────────────────────────────────────────────────────────────────
const StepExperience = ({ formData, handleInputChange }) => (
  <div className="space-y-5">
    <div className="rounded-lg border border-slate-200 p-4">
      <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-500">
        Previous Employment
      </p>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        {[
          ['totalExperience',           'Total Experience',   'text'],
          ['previousCompanyName',       'Company Name',       'text'],
          ['previousCompanyLocation',   'Location',           'text'],
          ['previousJobTitle',          'Job Title',          'text'],
          ['previousSupervisorName',    'Supervisor Name',    'text'],
          ['previousSupervisorContact', 'Supervisor Contact', 'text'],
          ['reasonForLeaving',          'Reason for Leaving', 'text'],
        ].map(([field, label, type]) => (
          <div key={field}>
            <label className="mb-1 block text-xs font-medium text-slate-600">{label}</label>
            <input type={type} value={formData[field]}
              onChange={(e) => handleInputChange(field, e.target.value)} className={inputCls} />
          </div>
        ))}
        <div>
          <label className="mb-1 block text-xs font-medium text-slate-600">Start Date</label>
          <input type="date" value={formData.previousStartDate}
            onChange={(e) => handleInputChange('previousStartDate', e.target.value)} className={inputCls} />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-slate-600">End Date</label>
          <input type="date" value={formData.previousEndDate}
            onChange={(e) => handleInputChange('previousEndDate', e.target.value)} className={inputCls} />
        </div>
        <div className="flex items-center gap-2 pt-6">
          <input id="relieving" type="checkbox"
            checked={formData.relievingLetterSubmitted}
            onChange={(e) => handleInputChange('relievingLetterSubmitted', e.target.checked)}
            className="h-4 w-4 rounded border-slate-300 text-primary-600 focus:ring-primary-500" />
          <label htmlFor="relieving" className="text-xs font-medium text-slate-700">
            Relieving Letter Submitted
          </label>
        </div>
      </div>
    </div>

    {[
      { prefix: 'reference1', heading: 'Reference 1' },
      { prefix: 'reference2', heading: 'Reference 2' },
    ].map(({ prefix, heading }) => (
      <div key={prefix} className="rounded-lg border border-slate-200 p-4">
        <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-500">{heading}</p>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
          {['Name', 'Address', 'Mobile', 'Relationship'].map((label) => {
            const field = `${prefix}${label}`;
            return (
              <div key={field}>
                <label className="mb-1 block text-xs font-medium text-slate-600">{label}</label>
                <input type="text" value={formData[field]}
                  onChange={(e) => handleInputChange(field, e.target.value)} className={inputCls} />
              </div>
            );
          })}
        </div>
      </div>
    ))}
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// STEP 6 – DOCUMENTS
//
// KEY FIX: employeeId arrives via a REF (newEmployeeIdRef.current) from the
// parent, not via state. Refs update synchronously — before React re-renders —
// so this component always receives the real MongoDB _id on its very first
// paint. The old "Employee is being created…" amber banner is gone.
//
// The file input is visually hidden (sr-only) and triggered by a styled
// <label htmlFor="doc-file-input"> — this is what opens the OS file manager
// when the user clicks "Click to browse file…".
// ─────────────────────────────────────────────────────────────────────────────
const StepDocuments = ({ employeeId, employeeName }) => {
  const pendingIdRef = React.useRef(0);
  const savingRef = React.useRef(false);
  const [pendingDocs, setPendingDocs] = React.useState([]);
  const [selectedDocType, setSelectedDocType] = React.useState('');
  const [customDocumentLabel, setCustomDocumentLabel] = React.useState('');
  const [selectedFile,    setSelectedFile]    = React.useState(null);
  const [uploadedDocs,    setUploadedDocs]    = React.useState([]);
  const [uploading,       setUploading]       = React.useState(false);
  const [loadingDocs,     setLoadingDocs]     = React.useState(true);
  const [deleting,        setDeleting]        = React.useState({});

  React.useEffect(() => {
    if (!employeeId) return;
    const fetchDocuments = async () => {
      try {
        setLoadingDocs(true);
        const response = await api.get(`/documents/employee/${employeeId}`);
        setUploadedDocs(response.data.documents || []);
      } catch (error) {
        console.error('Error fetching documents:', error);
        toast.error('Failed to load documents');
        setUploadedDocs([]);
      } finally {
        setLoadingDocs(false);
      }
    };
    fetchDocuments();
  }, [employeeId]);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!['application/pdf', 'image/jpeg', 'image/png'].includes(file.type)) {
      toast.error('Only PDF, JPG, and PNG files are allowed');
      e.target.value = '';
      return;
    }
    if (file.size > MAX_FILE_SIZE_BYTES) {
      toast.error(`File size must be less than ${MAX_FILE_MB}MB`);
      e.target.value = '';
      return;
    }
    setSelectedFile(file);
  };

  const handleAddDocument = () => {
    if (!selectedDocType.trim()) {
      toast.error('Please select a document type');
      return;
    }
    if (selectedDocType === DOCUMENT_TYPE_OTHER) {
      const t = customDocumentLabel.trim();
      if (t.length < DOCUMENT_LABEL_MIN_LENGTH) {
        toast.error(`Enter a document name (${DOCUMENT_LABEL_MIN_LENGTH}–${DOCUMENT_LABEL_MAX_LENGTH} characters)`);
        return;
      }
      if (t.length > DOCUMENT_LABEL_MAX_LENGTH) {
        toast.error(`Document name must be at most ${DOCUMENT_LABEL_MAX_LENGTH} characters`);
        return;
      }
    }
    if (!selectedFile) {
      toast.error('Please select a file');
      return;
    }
    const trimmedCustom = customDocumentLabel.trim();
    pendingIdRef.current += 1;
    setPendingDocs((prev) => [
      ...prev,
      {
        id: pendingIdRef.current,
        docType: selectedDocType,
        docTypeLabel:
          selectedDocType === DOCUMENT_TYPE_OTHER
            ? trimmedCustom
            : DOCUMENT_TYPE_OPTIONS.find((d) => d.value === selectedDocType)?.label || selectedDocType,
        documentLabel: selectedDocType === DOCUMENT_TYPE_OTHER ? trimmedCustom : '',
        file: selectedFile,
      },
    ]);
    setSelectedDocType('');
    setCustomDocumentLabel('');
    setSelectedFile(null);
    const fi = document.getElementById('doc-file-input');
    if (fi) fi.value = '';
  };

  const handleRemovePending = (id) => {
    setPendingDocs((prev) => prev.filter((d) => d.id !== id));
  };

  const handleSaveAll = async () => {
    if (savingRef.current) return;
    if (pendingDocs.length === 0) {
      toast.error('No documents to upload. Add documents first.');
      return;
    }
    savingRef.current = true;
    setUploading(true);
    try {
      let successCount = 0;
      const failedIds = [];

      for (const doc of pendingDocs) {
        try {
          const fd = new FormData();
          fd.append('documentType', doc.docType);
          if (doc.docType === DOCUMENT_TYPE_OTHER && doc.documentLabel) {
            fd.append('documentLabel', doc.documentLabel);
          }
          fd.append('file', doc.file);
          const response = await api.post(
            `/documents/employee/${employeeId}/upload`,
            fd,
            uploadRequestConfig()
          );
          setUploadedDocs((prev) => [response.data.document, ...prev]);
          successCount++;
        } catch (error) {
          console.error(`Upload error for ${doc.docTypeLabel}:`, error);
          failedIds.push(doc.id);
        }
      }

      if (successCount > 0) {
        toast.success(`${successCount} document(s) uploaded successfully`);
      }
      if (failedIds.length > 0) {
        toast.error(`${failedIds.length} document(s) failed to upload. Please retry.`);
      }
      setPendingDocs((prev) => prev.filter((d) => failedIds.includes(d.id)));
    } finally {
      setUploading(false);
      savingRef.current = false;
    }
  };

  const handleDelete = async (docId, titleForConfirm) => {
    if (!window.confirm(`Delete “${titleForConfirm}” document?`)) return;
    setDeleting((prev) => ({ ...prev, [docId]: true }));
    try {
      await api.delete(`/documents/${docId}`);
      setUploadedDocs((prev) => prev.filter((d) => d._id !== docId));
      toast.success('Document deleted');
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Failed to delete document');
    } finally {
      setDeleting((prev) => ({ ...prev, [docId]: false }));
    }
  };

  if (!employeeId) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="text-center">
          <div className="inline-block animate-spin text-3xl mb-3">⏳</div>
          <p className="text-sm text-slate-600">Setting up employee record…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">

      {/* Info banner */}
      <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
        <p className="text-sm font-semibold text-blue-900 mb-0.5">Upload Employee Documents</p>
        <p className="text-xs text-blue-700">
          Employee <strong>{employeeName}</strong> has been created successfully.
          Add documents using the <strong>+ Add</strong> button, then click <strong>Save All</strong> to upload.
        </p>
      </div>

      {/* Add document form */}
      <div className="rounded-lg border border-slate-200 bg-slate-50 p-5 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">

          <div>
            <label className="mb-1.5 block text-sm font-medium text-slate-700">
              Document Type *
            </label>
            <select value={selectedDocType}
              onChange={(e) => {
                const v = e.target.value;
                setSelectedDocType(v);
                if (v !== DOCUMENT_TYPE_OTHER) setCustomDocumentLabel('');
              }}
              className={inputCls}>
              <option value="">Select document type</option>
              {DOCUMENT_TYPE_OPTIONS.map((doc) => (
                <option key={doc.value} value={doc.value}>{doc.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-medium text-slate-700">
              Select File *
            </label>
            <label
              htmlFor="doc-file-input"
              className="flex items-center gap-2 w-full cursor-pointer rounded-lg border border-dashed border-slate-400 bg-white px-3 py-2 text-sm text-slate-500 hover:border-primary-500 hover:bg-primary-50 hover:text-primary-700 transition-colors"
            >
              <Upload className="h-4 w-4 flex-shrink-0" />
              <span className="truncate">
                {selectedFile ? selectedFile.name : 'Click to browse file…'}
              </span>
            </label>
            <input
              id="doc-file-input"
              type="file"
              accept=".pdf,.jpg,.jpeg,.png"
              onChange={handleFileChange}
              className="sr-only"
            />
            <p className="mt-1 text-xs text-slate-400">PDF, JPG, PNG · max {MAX_FILE_MB} MB</p>
          </div>

          <div>
            <button
              type="button"
              onClick={handleAddDocument}
              disabled={
                !selectedDocType
                || !selectedFile
                || (selectedDocType === DOCUMENT_TYPE_OTHER
                  && customDocumentLabel.trim().length < DOCUMENT_LABEL_MIN_LENGTH)
              }
              className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50 transition-colors h-10"
            >
              Upload
            </button>
          </div>
        </div>

        {selectedDocType === DOCUMENT_TYPE_OTHER && (
          <div>
            <label className="mb-1.5 block text-sm font-medium text-slate-700">Document name *</label>
            <input
              type="text"
              value={customDocumentLabel}
              onChange={(e) => setCustomDocumentLabel(e.target.value)}
              placeholder={`e.g. Contractor agreement (${DOCUMENT_LABEL_MIN_LENGTH}–${DOCUMENT_LABEL_MAX_LENGTH} characters)`}
              maxLength={DOCUMENT_LABEL_MAX_LENGTH}
              className={inputCls}
            />
          </div>
        )}

        {selectedFile && (
          <div className="flex items-center gap-3 rounded-lg border border-emerald-200 bg-white p-3">
            <CheckCircle className="h-5 w-5 text-emerald-500 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-900 truncate">{selectedFile.name}</p>
              <p className="text-xs text-slate-500">{formatFileSize(selectedFile.size)}</p>
            </div>
            <button type="button"
              onClick={() => {
                setSelectedFile(null);
                const fi = document.getElementById('doc-file-input');
                if (fi) fi.value = '';
              }}
              className="text-slate-400 hover:text-slate-600 flex-shrink-0">
              <X className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>

      {/* Pending documents queue */}
      {pendingDocs.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
            Pending Documents
            <span className="inline-flex items-center rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-700">
              {pendingDocs.length}
            </span>
          </h3>

          <div className="space-y-2">
            {pendingDocs.map((doc) => (
              <div
                key={doc.id}
                className="flex items-center justify-between rounded-lg border border-amber-200 bg-amber-50 p-3 hover:shadow-sm transition-shadow"
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <span className="text-lg flex-shrink-0">📎</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-900">{doc.docTypeLabel}</p>
                    <p className="text-xs text-slate-500 truncate">
                      {doc.file.name} &middot; {formatFileSize(doc.file.size)}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => handleRemovePending(doc.id)}
                  className="p-2 rounded-lg text-red-600 hover:bg-red-50 transition-colors flex-shrink-0"
                  title="Remove"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>

          <button
            type="button"
            onClick={handleSaveAll}
            disabled={uploading}
            className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-emerald-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50 transition-colors"
          >
            {uploading ? (
              <>
                <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                Uploading {pendingDocs.length} document(s)…
              </>
            ) : (
              <>Save All ({pendingDocs.length} document{pendingDocs.length > 1 ? 's' : ''})</>
            )}
          </button>
        </div>
      )}

      {/* Uploaded documents list */}
      <div className="space-y-3">
        <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
          Uploaded Documents
          {uploadedDocs.length > 0 && (
            <span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-600">
              {uploadedDocs.length}
            </span>
          )}
        </h3>

        {loadingDocs ? (
          <div className="flex items-center justify-center py-8">
            <LoadingSpinner size="lg" />
          </div>
        ) : uploadedDocs.length === 0 ? (
          <div className="rounded-lg border-2 border-dashed border-slate-300 p-8 text-center">
            <File className="mx-auto h-10 w-10 text-slate-300 mb-2" />
            <p className="text-sm text-slate-500">No documents uploaded yet. Add documents above and click Save All.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {uploadedDocs.map((doc) => (
              <div key={doc._id}
                className="flex items-center justify-between rounded-lg border border-slate-200 bg-white p-3 hover:shadow-sm transition-shadow">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <span className="text-xl flex-shrink-0">
                    {doc.mimeType === 'application/pdf' ? '📄' : '🖼️'}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-900">
                      {documentDisplayName(doc, DOCUMENT_TYPE_OPTIONS)}
                    </p>
                    <p className="text-xs text-slate-500 truncate">{doc.originalFileName}</p>
                    <p className="text-xs text-slate-400">{formatFileSize(doc.size)}</p>
                  </div>
                </div>
                <div className="flex items-center gap-1 ml-3 flex-shrink-0">
                  <button type="button" onClick={() => viewDocument(doc._id)}
                    className="p-2 rounded-lg text-blue-600 hover:bg-blue-50 transition-colors"
                    title="View">
                    <Eye className="h-4 w-4" />
                  </button>
                  <button type="button" onClick={() => downloadDocument(doc._id)}
                    className="p-2 rounded-lg text-blue-600 hover:bg-blue-50 transition-colors"
                    title="Download">
                    <Download className="h-4 w-4" />
                  </button>
                  <button onClick={() => handleDelete(doc._id, documentDisplayName(doc, DOCUMENT_TYPE_OPTIONS))}
                    disabled={deleting[doc._id]}
                    className="p-2 rounded-lg text-red-500 hover:bg-red-50 disabled:opacity-50 transition-colors"
                    title="Delete">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const STEP_PANELS = [StepPersonal, StepAddress, StepPayroll, StepEducation, StepExperience, StepDocuments];

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
const Onboarding = () => {

  const [employees,   setEmployees]   = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [searchTerm,  setSearchTerm]  = useState('');

  const [showFormModal,      setShowFormModal]      = useState(false);
  const [currentStep,        setCurrentStep]        = useState(1);
  const [submitting,         setSubmitting]         = useState(false);
  const [generatingEmployeeId, setGeneratingEmployeeId] = useState(false);
  const [formData,           setFormData]           = useState(INITIAL_FORM_DATA);

  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [newEmployeeId,     setNewEmployeeId]     = useState(null);
  const [newEmployeeName,   setNewEmployeeName]   = useState('');

  // ── REFS for Step 6 ──────────────────────────────────────────
  // Written synchronously in handleNext BEFORE setState calls.
  // stepProps reads from these so Step 6 always gets the real
  // employeeId on its very first render — never sees null.
  const newEmployeeIdRef   = useRef(null);
  const newEmployeeNameRef = useRef(null);

  // ── Fetch employee list ──────────────────────────────────────
  const fetchEmployees = async () => {
    try {
      const response = await api.get('/admin/employees');
      setEmployees(normalizeUserProfiles(response.data.employees || []));
    } catch (error) {
      console.error('fetchEmployees error:', error);
      toast.error('Failed to load employee records');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchEmployees(); }, []);

  // ── Modal helpers ────────────────────────────────────────────
  const openCreateModal = () => {
    setFormData(INITIAL_FORM_DATA);
    setCurrentStep(1);
    newEmployeeIdRef.current   = null;
    newEmployeeNameRef.current = null;
    setNewEmployeeId(null);
    setNewEmployeeName('');
    setShowFormModal(true);
  };

  const closeCreateModal = () => {
    setShowFormModal(false);
    setFormData(INITIAL_FORM_DATA);
    setCurrentStep(1);
    newEmployeeIdRef.current   = null;
    newEmployeeNameRef.current = null;
    setNewEmployeeId(null);
    setNewEmployeeName('');
  };

  const closeDocumentModal = () => {
    setShowDocumentModal(false);
    newEmployeeIdRef.current   = null;
    newEmployeeNameRef.current = null;
    setNewEmployeeId(null);
    setNewEmployeeName('');
    setLoading(true);
    fetchEmployees();
  };

  // ── Input handlers ───────────────────────────────────────────
  const handleInputChange = (field, value) => {
    setFormData((prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'ctcBasic' || PAYROLL_PERCENTAGE_INPUT_FIELDS.includes(field)) {
        return { ...next, ...derivePayrollValues(next) };
      }
      return next;
    });
  };

  const handleOrganizationChange = async (organization) => {
    setFormData((prev) => ({ ...prev, organization, employeeId: '' }));
    if (!organization) return;
    setGeneratingEmployeeId(true);
    try {
      const res = await api.get('/admin/next-employee-id');
      setFormData((prev) => ({ ...prev, organization, employeeId: res.data?.employeeId || '' }));
    } catch (err) {
      console.error('next-employee-id error:', err);
      toast.error('Could not generate employee ID for selected organization.');
    } finally {
      setGeneratingEmployeeId(false);
    }
  };

  // ── Per-step validation ──────────────────────────────────────
  const validateStep = (stepIndex) => {
    const { requiredFields } = STEPS[stepIndex - 1];
    for (const field of requiredFields) {
      const v = formData[field];
      if (v === '' || v === null || v === undefined)
        return `${FIELD_LABELS[field] || field} is required`;
    }
    if (stepIndex === 1) {
      if (formData.password.length < 6)
        return 'Password must be at least 6 characters.';
      if (!['male','female','other'].includes(formData.gender))
        return 'Please select a valid gender.';
      if (!['single','married'].includes(formData.maritalStatus))
        return 'Please select a valid marital status.';
      if (formData.employeeId.length < 3 || formData.employeeId.length > 20)
        return 'Employee ID must be between 3 and 20 characters.';
    }
    if (stepIndex === 3) {
      for (const f of NON_NEGATIVE_NUMBER_FIELDS) {
        if (formData[f] !== '' && Number(formData[f]) < 0)
          return `${FIELD_LABELS[f] || f} cannot be negative.`;
      }
      for (const f of PAYROLL_PERCENTAGE_INPUT_FIELDS) {
        if (formData[f] === '')
          return `${FIELD_LABELS[f] || f} is required`;
        if (Number(formData[f]) < 0)
          return `${FIELD_LABELS[f] || f} cannot be negative.`;
      }
    }
    if (stepIndex === 4) {
      for (const f of YEAR_FIELDS) {
        const y = formData[f];
        if (y !== '' && (Number(y) < 1900 || Number(y) > 2100))
          return 'Year must be between 1900 and 2100.';
      }
    }
    return null;
  };

  // ── Next ─────────────────────────────────────────────────────
  const handleNext = async () => {
    const err = validateStep(currentStep);
    if (err) { toast.error(err); return; }

    if (currentStep === 5) {
      try {
        setSubmitting(true);
        const response = await api.post('/onboarding/employee', buildPayload());
        const created  = response.data.employee || response.data;
        const createdId = getUserIdentifier(created);

        // ✅ Write refs and flush state synchronously.
        // Refs update first, then flushSync forces React to commit the DOM
        // immediately. When Step 6 renders, it reads refs which have the real ID.
        newEmployeeIdRef.current   = createdId;
        newEmployeeNameRef.current = created.name;

        flushSync(() => {
          setNewEmployeeId(createdId);
          setNewEmployeeName(created.name);
          setCurrentStep(6);
        });

        toast.success('Employee created — pending Super Admin approval. You can upload documents below.');
      } catch (error) {
        console.error('Employee creation failed:', error);
        const errData = error.response?.data;
        toast.error(errData?.errors?.[0]?.msg || errData?.message || 'Failed to create employee.');
      } finally {
        setSubmitting(false);
      }
    } else {
      setCurrentStep((s) => Math.min(s + 1, STEPS.length));
    }
  };

  const handleBack = () => setCurrentStep((s) => Math.max(s - 1, 1));

  // ── Payload builder ──────────────────────────────────────────
  const buildPayload = () => {
    const payload = {};
    Object.entries(formData).forEach(([key, value]) => {
      if (PAYROLL_PERCENTAGE_INPUT_FIELDS.includes(key)) return;
      if (typeof value === 'boolean') { payload[key] = value; return; }
      payload[key] = NUMERIC_FIELDS.includes(key) ? Number(value) : value;
    });
    return payload;
  };

  // ── Final submit ─────────────────────────────────────────────
  const handleSubmit = async (event) => {
    event.preventDefault();
    if (currentStep === STEPS.length) {
      toast.success('Employee created — pending Super Admin approval');
      setLoading(true);
      fetchEmployees();
      closeCreateModal();
    }
  };

  // ── Derived values ───────────────────────────────────────────
  const filteredEmployees = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return employees;
    return employees.filter((emp) =>
      emp.name?.toLowerCase().includes(term) ||
      emp.email?.toLowerCase().includes(term) ||
      emp.employeeId?.toLowerCase().includes(term) ||
      emp.organization?.toLowerCase().includes(term)
    );
  }, [employees, searchTerm]);

  const activeCount   = employees.filter((e) => e.isActive).length;
  const inactiveCount = employees.length - activeCount;

  // ✅ Read from REFS not state — guarantees Step 6 gets real values
  const stepProps = {
    formData,
    handleInputChange,
    handleOrganizationChange,
    generatingEmployeeId,
    employeeId:   newEmployeeIdRef.current,
    employeeName: newEmployeeNameRef.current,
  };
  const ActivePanel = STEP_PANELS[currentStep - 1];

  // ─────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6">

      {/* PAGE HEADER */}
      <div className="flex flex-col gap-4 rounded-xl border border-slate-200 bg-gradient-to-r from-slate-50 to-white p-6 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Employee Onboarding</h1>
          <p className="mt-1 text-sm text-slate-600">
            Add new employees using the onboarding form. Existing employees are listed below for reference.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={() => { setLoading(true); fetchEmployees(); }}
            className="inline-flex items-center rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100">
            <RefreshCw className="mr-2 h-4 w-4" />
            Refresh
          </button>
          <button type="button" onClick={openCreateModal}
            className="inline-flex items-center rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700">
            <UserPlus className="mr-2 h-4 w-4" />
            Add New Employee
          </button>
        </div>
      </div>

      {/* STATS */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Total Employees</p>
          <p className="mt-2 text-2xl font-semibold text-slate-900">{employees.length}</p>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Active</p>
          <p className="mt-2 text-2xl font-semibold text-emerald-600">{activeCount}</p>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Inactive</p>
          <p className="mt-2 text-2xl font-semibold text-rose-600">{inactiveCount}</p>
        </div>
      </div>

      {/* SEARCH */}
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input type="text"
            placeholder="Search by name, email, employee ID, or organization"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full rounded-lg border border-slate-300 py-2 pl-10 pr-3 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500" />
        </div>
      </div>

      {/* EMPLOYEE TABLE */}
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <div className="border-b border-slate-200 px-4 py-3">
          <h3 className="text-sm font-semibold text-slate-900">
            Current Employees ({filteredEmployees.length})
          </h3>
        </div>
        {loading ? (
          <LoadingSpinner size="lg" className="py-8" />
        ) : filteredEmployees.length === 0 ? (
          <div className="px-4 py-10 text-center text-sm text-slate-500">No employee records found.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">Employee</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">Employee ID</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">Organization</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {filteredEmployees.map((employee) => (
                  <tr key={getUserIdentifier(employee) || employee.email} className="hover:bg-slate-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center">
                        <div className="mr-3 flex h-9 w-9 items-center justify-center rounded-full bg-primary-100">
                          <Users className="h-4 w-4 text-primary-700" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-slate-900">{employee.name}</p>
                          <p className="text-xs text-slate-500">
                            <Mail className="mr-1 inline h-3 w-3" />
                            {employee.email}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-700">{employee.employeeId || 'N/A'}</td>
                    <td className="px-4 py-3 text-sm text-slate-700">
                      <Building2 className="mr-1 inline h-4 w-4 text-slate-400" />
                      {employee.organization || 'N/A'}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${
                        employee.isActive ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'
                      }`}>
                        {employee.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* FOOTER */}
      <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-4 text-sm text-slate-600">
        <p>
          Existing employee editing is available on the{' '}
          <Link to="/admin/employees" className="font-medium text-primary-700 underline">
            Employees page
          </Link>.
        </p>
      </div>

      {/* ─── ONBOARDING MODAL ─── */}
      {showFormModal && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-slate-900/60 px-4 py-8">
          <div className="w-full max-w-4xl rounded-2xl bg-white shadow-2xl">

            <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
              <div>
                <h2 className="text-xl font-semibold text-slate-900">New Employee Onboarding</h2>
                <p className="text-sm text-slate-500">
                  Step {currentStep} of {STEPS.length} —{' '}
                  <span className="font-medium text-slate-700">
                    {STEPS[currentStep - 1].description}
                  </span>
                </p>
              </div>
              <button type="button" onClick={closeCreateModal}
                className="rounded-md p-2 text-slate-500 hover:bg-slate-100 hover:text-slate-700">
                <X className="h-5 w-5" />
              </button>
            </div>

            <StepHeader currentStep={currentStep} />

            <form onSubmit={handleSubmit}>
              <div className="min-h-[320px] overflow-y-auto px-6 py-5">
                {currentStep === 6 ? (
                  <StepDocuments
                    employeeId={newEmployeeIdRef.current || newEmployeeId}
                    employeeName={newEmployeeNameRef.current || newEmployeeName}
                  />
                ) : (
                  <ActivePanel {...stepProps} />
                )}
              </div>

              <div className="flex items-center justify-between border-t border-slate-200 px-6 py-4">

                {/* Back — hidden on Step 6 (employee already in DB) */}
                {currentStep < STEPS.length ? (
                  <button type="button" onClick={handleBack}
                    disabled={currentStep === 1}
                    className="inline-flex items-center rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-40">
                    <ChevronLeft className="mr-1 h-4 w-4" />
                    Back
                  </button>
                ) : (
                  <div />
                )}

                <span className="text-xs text-slate-400">
                  {currentStep < STEPS.length
                    ? 'Fill required (*) fields then click Next.'
                    : 'Upload documents above, then click Finish.'}
                </span>

                {currentStep < STEPS.length ? (
                  <button type="button" onClick={handleNext}
                    disabled={submitting || generatingEmployeeId}
                    className="inline-flex items-center rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-60">
                    {submitting ? (
                      <>
                        <span className="mr-2 inline-block h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                        Creating…
                      </>
                    ) : (
                      <>Next <ChevronRight className="ml-1 h-4 w-4" /></>
                    )}
                  </button>
                ) : (
                  <button type="submit"
                    className="inline-flex items-center rounded-lg bg-emerald-600 px-5 py-2 text-sm font-medium text-white hover:bg-emerald-700">
                    Finish Onboarding
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ─── STANDALONE DOCUMENT MODAL ─── */}
      {showDocumentModal && newEmployeeId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-slate-900/60 px-4 py-8">
          <div className="w-full max-w-2xl rounded-2xl bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
              <h2 className="text-xl font-semibold text-slate-900">Upload Documents</h2>
              <button type="button" onClick={closeDocumentModal}
                className="rounded-md p-2 text-slate-500 hover:bg-slate-100">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-6">
              <StepDocuments employeeId={newEmployeeId} employeeName={newEmployeeName} />
            </div>
            <div className="flex justify-end border-t border-slate-200 px-6 py-4">
              <button type="button" onClick={closeDocumentModal}
                className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100">
                Done
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default Onboarding;